<script setup lang="ts">
import { reactive, onMounted, onBeforeUnmount, ref } from 'vue'

// =============================================================================
// PROPS
// =============================================================================

interface Props {
  apiEndpoint?: string
}

const props = withDefaults(defineProps<Props>(), {
  apiEndpoint: '/api/v2/innovationarticles/'
})

// =============================================================================
// TYPESCRIPT INTERFACES
// =============================================================================

interface HeroImage {
  url: string
  full_url: string
  width: number
  height: number
  alt: string
}

interface Innovation {
  id: number
  title: string
  innovation_date: string
  meta: {
    html_url: string
  }
  hero?: HeroImage
}

interface ApiResponse {
  meta: { total_count: number }
  items: Innovation[]
}

// =============================================================================
// REACTIVE STATE
// =============================================================================

const state = reactive({
  innovations: [] as Innovation[],
  loading: false,
  error: null as string | null,
})

const scrollContainer = ref<HTMLElement | null>(null)
let scrollInterval: number | null = null
let isAutoScrolling = true

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getInnovationYear(dateString: string): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.getFullYear().toString()
}

// =============================================================================
// API COMMUNICATION
// =============================================================================

async function fetchInnovations() {
  state.loading = true
  state.error = null

  try {
    const response = await fetch(props.apiEndpoint)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data: ApiResponse = await response.json()
    state.innovations = data.items
  } catch (err) {
    state.error = err instanceof Error ? err.message : 'Failed to fetch innovations'
  } finally {
    state.loading = false
  }
}

// =============================================================================
// AUTO SCROLL FUNCTIONALITY
// =============================================================================

function startAutoScroll() {
  if (scrollInterval) return

  scrollInterval = window.setInterval(() => {
    if (!scrollContainer.value || !isAutoScrolling) return

    const container = scrollContainer.value
    const maxScroll = container.scrollWidth - container.clientWidth

    // Scroll by 1 pixel for smooth scrolling
    container.scrollLeft += 1

    // Loop back to the beginning when reaching the end
    if (container.scrollLeft >= maxScroll) {
      container.scrollLeft = 0
    }
  }, 30) // 30ms interval for smooth scrolling (~33fps)
}

function stopAutoScroll() {
  isAutoScrolling = false
  if (scrollInterval) {
    clearInterval(scrollInterval)
    scrollInterval = null
  }
}

function handleUserInteraction() {
  stopAutoScroll()
}

// =============================================================================
// LIFECYCLE HOOKS
// =============================================================================

onMounted(() => {
  fetchInnovations()

  // Start auto-scroll after innovations are loaded
  setTimeout(() => {
    if (state.innovations.length > 0) {
      startAutoScroll()
    }
  }, 1000) // Delay to ensure content is rendered
})

onBeforeUnmount(() => {
  stopAutoScroll()
})
</script>

<template>
  <div class="innovations-list-container pt--8">
    <!-- Loading state -->
    <div
      v-if="state.loading"
      class="text-gray-600 py-4"
    >
      Loading innovations...
    </div>
    <!-- Error state -->
    <div
      v-else-if="state.error"
      class="text-red-600 py-4"
    >
      {{ state.error }}
    </div>
    <!-- Horizontal scrollable list -->
    <div
      v-else-if="state.innovations.length > 0"
      ref="scrollContainer"
      class="innovations-list overflow-x-auto flex"
      @mousedown="handleUserInteraction"
      @touchstart="handleUserInteraction"
      @wheel="handleUserInteraction"
    >
      <div
        v-for="innovation in state.innovations"
        :key="innovation.id"
        class="innovation-card shrink-0 bg-gray-25 block ring ring-gray-100 overflow-hidden w-66 md:w-88 first:ml-px last:mr-px my-px"
      >
        <a
          :href="innovation.meta.html_url"
          class="block"
        >
          <img
            v-if="innovation.hero"
            :src="innovation.hero.url"
            :alt="innovation.hero.alt"
            class="aspect-video w-full object-cover"
          />
          <div
            v-else
            class="aspect-video w-full bg-gray-200"
          />
        </a>
        <div class="pt-4 px-5 pb-8 flex flex-col justify-start items-start gap-4 h-full">
          <div class="flex flex-col justify-start items-start gap-2">
            <div class="text-red-600 text-2xl font-semibold font-['Open_Sans'] leading-8 wrap-break-word">
              {{ getInnovationYear(innovation.innovation_date) }}
            </div>
            <div class="text-gray-700 text-base font-normal font-['Open_Sans'] leading-6 line-clamp-4 wrap-break-word">
              {{ innovation.title }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Empty state -->
    <div
      v-else
      class="text-gray-600 py-4"
    >
      No innovations available.
    </div>
  </div>
</template>

<style scoped>
.innovations-list {
  scrollbar-width: thin;
  scrollbar-color: #9ca3af #f3f4f6;
}

.innovations-list::-webkit-scrollbar {
  height: 8px;
}

.innovations-list::-webkit-scrollbar-track {
  background: #f3f4f6;
}

.innovations-list::-webkit-scrollbar-thumb {
  background-color: #9ca3af;
  border-radius: 4px;
}

.innovations-list::-webkit-scrollbar-thumb:hover {
  background-color: #6b7280;
}
</style>
